/** @jsx jsx */

import { ReactNode } from 'react';

import { Text } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import LinkTabs from 'app/developer-v3/components/LinkTabs';
import { LayoutStyles } from 'app/developer-v3/layouts/constants';
import { getAppUrl } from 'app/developer-v3/utils';
import {
  useQueryIntegration,
  useQueryIntegrationToken,
  useQueryIssues,
  useRouter,
} from 'app/developer-v3/hooks';

const Styles = {
  root: css`
    height: 100%;
    display: flex;
    flex: 1;
    flex-flow: column nowrap;
  `,
  header: css`
    flex: 0;
    padding: ${LayoutStyles.verticalPadding} ${LayoutStyles.horizontalPadding} 0
      ${LayoutStyles.horizontalPadding};
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  `,
};

type Props = {
  children: ReactNode;
};

function IssuesPage({ children }: Props) {
  const { params, query, url } = useRouter();
  const integrationId = Number(params.id);

  // Parse filters from URL query params
  const triggerKeys = query.triggers?.split(',') ?? [];
  const actionKeys = query.actions?.split(',') ?? [];
  const typeOf =
    query['issue-type'] === 'bug'
      ? 'bug'
      : query['issue-type'] === 'feature-request'
      ? 'feature-request'
      : undefined;

  // Fetch count for the header badge (filtered to match the list)
  const { data: issuesData } = useQueryIssues(
    {
      actions: [...triggerKeys, ...actionKeys],
      integrationId,
      orderBy: 'created_at',
      orderDirection: 'DESC',
      page: 1,
      typeOf,
    },
    { staleTime: 60000 }
  );

  const count = issuesData?.count ?? 0;

  // Check if integration is published (beta or public)
  const { data: integration } = useQueryIntegration({ integrationId });
  const isPublished = integration?.isBeta || integration?.public;

  // Check if token is available
  // TODO: Remove token check once tokens are backfilled (end of February 2026)
  const { data: tokenData } = useQueryIntegrationToken(
    { integrationId },
    { staleTime: 300000 }
  );
  const hasToken = Boolean(tokenData?.token);

  return (
    <div css={Styles.root}>
      <header css={Styles.header}>
        <Text fontWeight={600} tag="h1" type="PageHeader">
          Bugs & feature requests
        </Text>
        <LinkTabs
          currentUrl={url}
          items={[
            {
              badgeText: count ? `${count}` : undefined,
              disabled: false,
              href: `${getAppUrl(integrationId)}/issues`,
              text: 'Issues',
            },
            ...(isPublished && hasToken
              ? [
                  {
                    // TODO: Remove NEW badge once tokens are backfilled (end of February 2026)
                    badgeText: 'NEW',
                    disabled: false,
                    href: `${getAppUrl(
                      integrationId
                    )}/issues/zapier-issue-manager`,
                    text: 'Zapier Issue Manager',
                  },
                ]
              : []),
          ]}
        />
      </header>
      {children}
    </div>
  );
}

export default IssuesPage;
